import { Icon as IconifyIcon } from '@iconify/react/dist/iconify.js';
import { styled } from '@linaria/react';
import { Tabs } from '@mantine/core';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PageLayout } from '../../components/page-layout/page-layout';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { baseWhite, inkLight, secondaryBase, skyLightest } from '../../styles/design-tokens';
import { Button } from '../../ui-library/button/button';
import { Icon } from '../../ui-library/icon/icon';
import { H3 } from '../../ui-library/typography/typography';
import { MenuDrivenContainer } from '../menu-driven-container/menu-driven-container';
import { ApiKeysTab } from './components/api-keys-tab';

enum SettingsTab {
  ApiKeys = 'api-keys',
}

const DEFAULT_TAB = SettingsTab.ApiKeys;
const VALID_TABS = new Set<SettingsTab>([SettingsTab.ApiKeys]);

/**
 * Top-level Settings page. Currently exposes a single "API Keys" tab where
 * the user can view, regenerate, and revoke their personal MCP token.
 *
 * The active tab is driven by the URL segment (`/application/settings/:tab`)
 * so direct links and reloads land on the correct tab.
 *
 * @returns {JSX.Element} The Settings page shell with tab navigation.
 */
export function Settings() {
  useDocumentTitle('Settings - Bloomfilter');

  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();

  const activeTab = useMemo<SettingsTab>(() => {
    if (tab && VALID_TABS.has(tab as SettingsTab)) {
      return tab as SettingsTab;
    }
    return DEFAULT_TAB;
  }, [tab]);

  useEffect(() => {
    if (!tab || !VALID_TABS.has(tab as SettingsTab)) {
      navigate(`/application/settings/${DEFAULT_TAB}`, { replace: true });
    }
  }, [tab, navigate]);

  const handleTabChange = useCallback(
    (value: string | null) => {
      if (!value) return;
      navigate(`/application/settings/${value}`);
    },
    [navigate],
  );

  const handleClickBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const tabs = (
    <StyledTabs variant="pills" radius="xl" value={activeTab} onChange={handleTabChange}>
      <Tabs.List>
        <Tabs.Tab
          value={SettingsTab.ApiKeys}
          leftSection={<IconifyIcon icon="material-symbols:key-outline" />}
          color={secondaryBase}
          data-bf="settings-tab-api-keys"
        >
          API Keys
        </Tabs.Tab>
      </Tabs.List>
    </StyledTabs>
  );

  return (
    <MenuDrivenContainer
      header={
        <SettingsHeader>
          <Button
            radius="xl"
            size="sm"
            variant="secondary"
            onClick={handleClickBack}
            leftSection={<Icon name="keyboard_backspace" color={baseWhite} />}
          >
            Back
          </Button>
          <Icon style={{ marginLeft: 8 }} name="settings" color={secondaryBase} variant="outlined" size={28} />
          <H3>Settings</H3>
        </SettingsHeader>
      }
    >
      <PageLayout
        controlBar={null}
        tabs={tabs}
        mainSection={activeTab === SettingsTab.ApiKeys ? <ApiKeysTab /> : null}
      />
    </MenuDrivenContainer>
  );
}

const SettingsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledTabs = styled(Tabs)`
  .mantine-Tabs-list {
    gap: 8px;
  }

  .mantine-Tabs-tab {
    transition: all 0.2s ease;
    font-weight: 500;
    font-size: 14px;
    padding: 8px 16px;
    height: auto;

    &[data-active] {
      color: ${baseWhite};
      background: ${secondaryBase};
    }

    &:not([data-active]) {
      color: ${inkLight};
      border: 1px solid ${inkLight};

      &:hover {
        background: ${skyLightest};
        border: 1px solid ${secondaryBase};
      }
    }
  }
`;
